import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { adminUiCopy } from '../i18n/adminUi';
import { useI18n } from '../i18n/provider';
import { Button } from './ui/Button';

interface Props {
  children: ReactNode;
  locationKey?: string;
  copy: {
    errorBoundaryTitle: string;
    errorBoundaryDescription: string;
    errorBoundaryTryAgain: string;
    errorBoundaryGoDashboard: string;
    errorBoundaryContact: string;
    support: string;
  };
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundaryInner extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidUpdate(prevProps: Props) {
    // Reset error state when the route changes
    if (this.state.hasError && prevProps.locationKey !== this.props.locationKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);

    // Auto-reload once on chunk load failure (stale deploy)
    const isChunkError =
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Loading CSS chunk');

    if (isChunkError && !sessionStorage.getItem('chunk_reload')) {
      sessionStorage.setItem('chunk_reload', '1');
      window.location.reload();
      return;
    }
    // Clear the flag so future deploys can trigger a reload again
    sessionStorage.removeItem('chunk_reload');
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-red-50 flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center">
            {/* Error Illustration */}
            <div className="mb-8">
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-red-100 mb-4">
                <svg
                  className="w-12 h-12 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">{this.props.copy.errorBoundaryTitle}</h1>
              <p className="text-slate-600 mb-4">
                {this.props.copy.errorBoundaryDescription}
              </p>

              {/* Error Details (development only) */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-left">
                  <p className="text-xs font-mono text-red-800 break-all">
                    {this.state.error.message}
                  </p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={this.handleReset} variant="outline" className="w-full sm:w-auto">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                {this.props.copy.errorBoundaryTryAgain}
              </Button>
              <Button
                className="w-full sm:w-auto"
                onClick={() => {
                  window.location.href = '/admin';
                }}
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                {this.props.copy.errorBoundaryGoDashboard}
              </Button>
            </div>

            {/* Help Text */}
            <div className="mt-8 pt-8 border-t border-slate-100">
              <p className="text-sm text-slate-500">
                {this.props.copy.errorBoundaryContact}{' '}
                <a
                  href="mailto:support@walletgate.app"
                  className="text-red-600 hover:text-red-700 font-medium"
                >
                  {this.props.copy.support}
                </a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Route-aware error boundary that automatically resets when the user navigates
 * to a different page, so a crash on one page doesn't block all other pages.
 */
export function ErrorBoundary({ children }: { children: ReactNode }): JSX.Element {
  const location = useLocation();
  const { locale } = useI18n();
  const copy = adminUiCopy[locale].shared;
  return (
    <ErrorBoundaryInner locationKey={location.key} copy={copy}>
      {children}
    </ErrorBoundaryInner>
  );
}
